import React, { useMemo, useState } from 'react';

import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  Stack,
  useTheme,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Card from '../../SystemDesign/Card';
import { TitleScreen, SubTitleScreen } from '../../SystemDesign/Styles';
import { tokens } from '../../theme';
import { useChangePasswordMutation } from '../../services/SkillsManagement';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';

export default function Settings() {
  // Form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  // Show/hide toggles
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // API
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Validation
  const errors = useMemo(() => {
    const e = {};
    if (!currentPassword) e.currentPassword = 'Current password is required.';
    if (!newPassword) e.newPassword = 'New password is required.';
    else if (newPassword.length < 8) e.newPassword = 'Minimum 8 characters.';
    if (!confirm) e.confirm = 'Please confirm your new password.';
    else if (confirm !== newPassword) e.confirm = 'Passwords do not match.';
    return e;
  }, [currentPassword, newPassword, confirm]);

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    try {
      await changePassword({
        changePasswordSchema: {
          currentPassword,
          newPassword,
        },
      }).unwrap();
      // Success: clear fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
      // Optionally show a toast via your SnackBarContext if desired
      // e.g., setInfoAlert({ severity: 'success', message: 'Password changed successfully.' }); setOpenSnackBar(true);
      // Force logout and redirect to login after password change
      await logout();
      navigate('/login');
    } catch (err) {
      // Optionally show a toast with error message
      // e.g., setInfoAlert({ severity: 'error', message: err?.data?.message || 'Failed to change password.' }); setOpenSnackBar(true);
      // Keep values so the user can correct and retry
      // For debugging:
      // console.error('Change password failed:', err);
    }
  };

  return (
    <Box className="container" sx={{ p: 3 }}>
      {/* Page Header (left-aligned like PostesList) */}
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <SettingsOutlinedIcon color="action" sx={{ mt: 0.5 }} />
          <Box>
            <Typography sx={TitleScreen(colors)}>Settings</Typography>
            <Typography sx={SubTitleScreen(colors)}>
              Manage your preferences and security settings
            </Typography>
          </Box>
        </Box>

        {/* Security Card centered */}
        <Grid container justifyContent="center">
          <Grid item size={{ xs: 12, md: 9, lg: 8 }}>
            <Card
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LockOutlinedIcon fontSize="small" />
                  <span>Security</span>
                </Box>
              }
              subtitle="Update your password and security settings"
              sx={{ mx: 'auto', backgroundColor: theme.palette.background.secondCard, p:3 }}
            >
              <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={1} sx={{ width: '100%' }}>
                <TextField
                    fullWidth
                    label="Current password"
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    onBlur={() => setShowErrors(true)}
                    error={showErrors && Boolean(errors.currentPassword)}
                    helperText={showErrors ? (errors.currentPassword || ' ') : ' '}
                    placeholder="Enter your current password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle current password visibility"
                            onClick={() => setShowCurrent((s) => !s)}
                            edge="end"
                          >
                            {showCurrent ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                <TextField
                    fullWidth
                    label="New password"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onBlur={() => setShowErrors(true)}
                    error={showErrors && Boolean(errors.newPassword)}
                    helperText={showErrors ? (errors.newPassword || ' ') : ' '}
                    placeholder="Enter your new password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle new password visibility"
                            onClick={() => setShowNew((s) => !s)}
                            edge="end"
                          >
                            {showNew ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                <TextField
                    fullWidth
                    label="Confirm new password"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    onBlur={() => setShowErrors(true)}
                    error={showErrors && Boolean(errors.confirm)}
                    helperText={showErrors ? (errors.confirm || ' ') : ' '}
                    placeholder="Confirm your new password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowConfirm((s) => !s)}
                            edge="end"
                          >
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                  >
                    {isLoading ? 'Changing…' : 'Change password'}
                  </Button>
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  </Box>
  );
}